import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Checkbox from 'components/Checkbox/checkbox'
import * as S from './ExportModal.styles'
import { hasIntersection } from 'utils/hasIntersection'
import { Modal } from '../../UI/Modal/Modal'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { useColumnStoreContext } from 'stores/ColumnsStore/useColumnsStoreContext'
import { useOptionalColumnStoreContext } from 'stores/ColumnsStore/useOptionalColumnsStoreContext'
import useApiService from 'services/api'
import { TableTypeSlug } from 'models/companies'
import { Column } from 'stores/ColumnsStore/ColumnsStore'

export interface ExportModalColumn {
  title: string
  elements: string[][]
}

/**
 * Reorder the checked export column ids so the file columns follow the user's
 * on-page column order instead of the static modal layout.
 *
 * The modal groups columns by category (Company Attributes, Forecast, ...), which
 * does not match the order the user arranged on the table. `selectedColumns` from
 * the ColumnsStore is maintained in on-page display order, so we sort by it.
 * Columns that are not on the page (e.g. always-selected columns, or columns enabled
 * only for the export) keep their relative modal-layout order and are appended after.
 */
export function orderByOnPageColumns(
  checkedColumnIds: string[],
  onPageColumns?: Column[]
): string[] {
  if (!onPageColumns?.length) return checkedColumnIds

  const onPageOrder = new Map(
    onPageColumns.map((col, index) => [col.id, index])
  )
  return [...checkedColumnIds].sort((a, b) => {
    const aIndex = onPageOrder.get(a)
    const bIndex = onPageOrder.get(b)
    // Both on the page: use on-page order.
    if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex
    // Only one on the page: it comes first, off-page columns trail behind.
    if (aIndex !== undefined) return -1
    if (bIndex !== undefined) return 1
    // Neither on the page: preserve the original (modal-layout) order.
    return 0
  })
}

/**
 * Pick the column list that reflects the table's current on-page order.
 *
 * `selectedColumns` is the user's arranged order, but it is empty/undefined on a
 * fresh page load when the user has not opened the Table Columns modal. In that
 * case the visible table order comes from `defaultSelectedColumns` (the store's
 * computed default, the same list the table renders from), so we fall back to
 * it. This keeps every table's export aligned with the visible columns without
 * overriding an explicit user reorder (FIR-7469).
 */
export function resolveOnPageColumns(
  selectedColumns: Column[] | undefined,
  defaultSelectedColumns: Column[] | undefined
): Column[] | undefined {
  return selectedColumns && selectedColumns.length > 0
    ? selectedColumns
    : defaultSelectedColumns
}

/**
 * Remap on-page column ids into the export field id space so the ordering keys
 * match the export `selected_columns`. Most tables persist export field ids on
 * the page already and pass no map (identity); the deals V2 table overrides its
 * react-table ids (e.g. `cc_company_name`) and passes `V2_COLUMN_ID_TO_EXPORT_ID`.
 * Returns a new array; unmapped ids pass through unchanged.
 */
export function mapColumnIdsToExportIds(
  columns: Column[] | undefined,
  columnIdToExportId?: Record<string, string>
): Column[] | undefined {
  if (!columns || !columnIdToExportId) return columns
  return columns.map((col) =>
    columnIdToExportId[col.id]
      ? { ...col, id: columnIdToExportId[col.id] }
      : col
  )
}

export interface ExportModalProps {
  show: boolean
  _handleClose: () => void
  count: number
  filters: Record<string, unknown>
  productSelected?: number
  selectedIds?: number[]
  excludeIds?: number[]
  tableTypeSlug: TableTypeSlug
  endpointArgs?: string[]
}

interface ExportModalPropsPrivate extends ExportModalProps {
  columns: ExportModalColumn[]
  title: string
  subTitle: string
  submitText: string
  overrideDataTypeName?: string
  endpointArgs?: string[]
  /** Column IDs that should always be selected by default, even if not in selectedColumns */
  alwaysSelectedColumns?: string[]
}

export default function ExportModal(props: ExportModalPropsPrivate) {
  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )

  const controller = useRef<AbortController>(new AbortController())
  const api = useApiService()

  const onSubmit = async (
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx'
  ) => {
    await api.commonExportTable(
      props.tableTypeSlug,
      {
        include_ids: props.selectedIds ?? [],
        exclude_ids: props.excludeIds ?? [],
        filter_params: { ...props.filters, product_id: props.productSelected },
        selected_columns: selectedColumns,
        export_type: exportType,
      },
      controller.current,
      props.overrideDataTypeName,
      props.endpointArgs
    )
  }

  useEffect(() => {
    return () => {
      controller.current.abort()
    }
  }, [])

  return (
    <BaseExportModal
      {...props}
      selectedColumns={selectedColumns}
      onSubmit={onSubmit}
      handleClose={props._handleClose}
    />
  )
}

export interface BaseExportModalProps {
  selectedColumns?: Column[]
  columns: ExportModalColumn[]
  onSubmit: (
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx'
  ) => Promise<void>
  show: boolean
  handleClose: () => void
  title: string
  subTitle: string
  submitText: string
  disableExportTypes?: boolean
  /** Column IDs that should always be selected by default, even if not in selectedColumns */
  alwaysSelectedColumns?: string[]
  /**
   * Optional map from on-page react-table column ids to export field ids, for
   * tables (only the deals V2 table today) that override their column ids. When
   * omitted, on-page ids are assumed to already equal export field ids.
   */
  columnIdToExportId?: Record<string, string>
}

/**
 * Compute which column IDs should start unchecked in the export modal.
 *
 * A column starts unchecked when the user has not selected it in the table
 * (it is absent from `selectedColumns`) AND it is not an always-selected
 * column. Always-selected columns (e.g. SKU) are frozen on, so they are never
 * added to the unchecked list regardless of the table's column selection.
 */
export function computeInitialUncheckedColumns(
  columns: ExportModalColumn[],
  selectedColumns: Column[],
  alwaysSelectedColumns: string[] = []
): string[] {
  const unchecked: string[] = []
  columns.forEach((column) => {
    column.elements.forEach((element) => {
      const id = element[0]
      const isAlwaysSelected = alwaysSelectedColumns.includes(id)
      if (!isAlwaysSelected && !selectedColumns.find((col) => col.id === id)) {
        unchecked.push(id)
      }
    })
  })
  return unchecked
}

/**
 * Compute the final list of column IDs to export given the current unchecked
 * list. Always-selected columns are forced into the result even if they
 * somehow ended up in the unchecked list, since the export always includes
 * them.
 */
export function computeSelectedColumnsForExport(
  columns: ExportModalColumn[],
  uncheckedList: string[],
  alwaysSelectedColumns: string[] = []
): string[] {
  const selected: string[] = []
  columns.forEach((column) => {
    column.elements.forEach((element) => {
      const id = element[0]
      if (alwaysSelectedColumns.includes(id) || !uncheckedList.includes(id)) {
        selected.push(id)
      }
    })
  })
  return selected
}

/**
 * Compute the unchecked list after the user clicks a group's "uncheck all"
 * toggle. Every column in the group is added to the unchecked list except
 * always-selected columns, which stay frozen on. Existing unchecked entries
 * are preserved and duplicates are avoided.
 */
export function computeUncheckedAfterGroupUncheck(
  prevUnchecked: string[],
  groupColumnIds: string[],
  alwaysSelectedColumns: string[] = []
): string[] {
  const toUncheck = groupColumnIds.filter(
    (id) => !alwaysSelectedColumns.includes(id) && !prevUnchecked.includes(id)
  )
  return [...prevUnchecked, ...toUncheck]
}

export function BaseExportModal(props: BaseExportModalProps) {
  const [exportType, setExportType] = useState<'csv' | 'xlsx'>('csv')

  const [uncheckedList, setUncheckedList] = useState<string[]>([])

  // On a fresh page load the user may not have opened the Table Columns modal,
  // so `selectedColumns` is still empty/undefined. Fall back to the store's
  // `defaultSelectedColumns` (the same list the table renders from) so the
  // export honors the visible on-page order even before any interaction
  // (FIR-7469). The read is optional: importer / sample-request callers render
  // outside a ColumnsStoreProvider and pass no `selectedColumns`, and must keep
  // working unchanged.
  const defaultSelectedColumns = useOptionalColumnStoreContext(
    (state) => state.defaultSelectedColumns
  )
  const effectiveColumns = useMemo(
    () =>
      mapColumnIdsToExportIds(
        resolveOnPageColumns(props.selectedColumns, defaultSelectedColumns),
        props.columnIdToExportId
      ),
    [props.selectedColumns, defaultSelectedColumns, props.columnIdToExportId]
  )

  useEffect(() => {
    if (!effectiveColumns) return

    setUncheckedList(
      computeInitialUncheckedColumns(
        props.columns,
        effectiveColumns,
        props.alwaysSelectedColumns
      )
    )
  }, [
    effectiveColumns,
    props.columns,
    props.alwaysSelectedColumns,
    setUncheckedList,
  ])

  const isAlwaysSelected = useCallback(
    (id: string) => props.alwaysSelectedColumns?.includes(id) ?? false,
    [props.alwaysSelectedColumns]
  )

  const manageUnchecked = (id: string) => {
    // Always-selected columns are frozen: they cannot be unchecked because the
    // export always includes them. Ignore toggle attempts on them.
    if (isAlwaysSelected(id)) return
    if (uncheckedList.includes(id)) {
      setUncheckedList((prevArray) => prevArray.filter((elem) => elem !== id))
    } else {
      setUncheckedList((prevArray) => [...prevArray, id])
    }
  }

  async function onSubmit() {
    // Collect the checked columns (always-selected stay frozen), then reorder
    // them to match the user's on-page column order.
    const selected = computeSelectedColumnsForExport(
      props.columns,
      uncheckedList,
      props.alwaysSelectedColumns
    )

    await props.onSubmit(
      orderByOnPageColumns(selected, effectiveColumns),
      exportType
    )

    if (props.show) props.handleClose()
  }

  return (
    <Modal
      size={'lg'}
      title={props.title}
      description={props.subTitle}
      open={props.show}
      onOpenChange={(open) => !open && props.handleClose()}
      onAccept={onSubmit}
      acceptButtonText={`${props.submitText}`}
      footerChildren={
        !props.disableExportTypes && (
          <SelectExportType
            exportType={exportType}
            setExportType={setExportType}
          />
        )
      }
    >
      <div className={'flex gap-3'}>
        {props.columns?.map((el) => (
          <div className={'flex-1'} key={el.title}>
            <div className={'flex items-center gap-2'}>
              <Checkbox
                boxSize={'lg'}
                checked={
                  !hasIntersection(
                    el.elements.map((it) => it[0]),
                    uncheckedList
                  )
                }
                onChange={() => {
                  if (
                    hasIntersection(
                      el.elements.map((it) => it[0]),
                      uncheckedList
                    )
                  ) {
                    setUncheckedList((prevArray) =>
                      prevArray.filter(
                        (elem) => !el.elements.map((el) => el[0]).includes(elem)
                      )
                    )
                  } else {
                    // Never uncheck always-selected columns via the group toggle.
                    setUncheckedList((prevArray) =>
                      computeUncheckedAfterGroupUncheck(
                        prevArray,
                        el.elements.map((it) => it[0]),
                        props.alwaysSelectedColumns
                      )
                    )
                  }
                }}
              />
              <S.ColumnLabel>{el.title}</S.ColumnLabel>
            </div>
            {el.elements.map((item) => {
              return (
                <ColumnItem
                  key={item[0]}
                  label={item[1]}
                  checked={!uncheckedList.includes(item[0])}
                  disabled={isAlwaysSelected(item[0])}
                  onChange={() => manageUnchecked(item[0])}
                />
              )
            })}
          </div>
        ))}
      </div>
    </Modal>
  )
}

function SelectExportType({
  exportType,
  setExportType,
}: {
  exportType: 'csv' | 'xlsx'
  setExportType: (type: 'csv' | 'xlsx') => void
}) {
  const _value = useMemo(() => {
    return { label: exportType.toUpperCase(), value: exportType }
  }, [exportType])

  return (
    <div className={'flex gap-3 justify-center'}>
      <div className="w-1/3">
        <label
          className="text-base font-medium text-gray-800 select-none mb-1"
          htmlFor="exportType"
        >
          Export Type
        </label>
        <ReactSelectWrapper
          name={'exportType'}
          options={[
            { label: 'CSV', value: 'csv' },
            { label: 'XLSX', value: 'xlsx' },
          ]}
          value={_value}
          onChange={(value) =>
            setExportType((value as { value: 'csv' | 'xlsx' }).value)
          }
        />
      </div>
    </div>
  )
}

function ColumnItem(props: {
  label: string
  onChange: (checked: boolean) => void
  checked?: boolean
  disabled?: boolean
}) {
  const checkBoxRef = useRef<HTMLInputElement>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange(e.target.checked)
  }

  const toggleChecked = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (props.disabled) return
    if ((e.target as HTMLElement).closest('label')) return
    e.preventDefault()
    checkBoxRef.current?.click()
  }

  return (
    <S.ColumnItemContainer checked={props.checked} onClick={toggleChecked}>
      <Checkbox
        ref={checkBoxRef}
        checked={props.checked}
        disabled={props.disabled}
        onChange={handleChange}
      />
      {props.label}
    </S.ColumnItemContainer>
  )
}
